import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';

export type ProductSubnavKind = 'ntrip' | 'analyzer' | 'ppk' | 'receivers' | 'software' | null;

@Component({
  selector: 'app-landing-header',
  templateUrl: './landing-header.component.html',
  styleUrl: './landing-header.component.scss'
})
export class LandingHeaderComponent implements OnInit {
  productSubnav: ProductSubnavKind = null;
  rtkExpertSiteUrl = '';

  readonly receiverLinks = [
    { label: 'X8-M1 RTK GNSS Receiver', anchor: 'receiver-x8-m1' },
    { label: 'RTU609BT RTK Heading GNSS Receiver', anchor: 'receiver-rtu609bt' },
    { label: 'RTU608BT Industrial-Grade RTK GNSS Receiver', anchor: 'receiver-rtu608bt' },
    { label: 'Y55G RTK/PPP GNSS Android Device', anchor: 'receiver-y55g' },
    { label: 'GC-9140KB USB-C GNSS Receiver', anchor: 'receiver-gc-9140kb' }
  ];

  constructor(private auth: AuthService, private router: Router, private userService: UserService) {
    this.syncFromUrl(this.router.url || '');
    this.router.events.subscribe(evt => {
      if (evt instanceof NavigationEnd) {
        this.syncFromUrl(evt.urlAfterRedirects || '');
      }
    });
  }

  ngOnInit(): void {
    this.userService.getPublicSiteUrls().subscribe({
      next: (cfg) => {
        this.rtkExpertSiteUrl = (cfg?.rtkExpertSiteURL || '').trim();
      },
      error: () => {
        this.rtkExpertSiteUrl = '';
      }
    });
  }

  get showProductSubnav(): boolean {
    return this.productSubnav != null;
  }

  private syncFromUrl(url: string): void {
    const path = (url || '').split('?')[0].split('#')[0];
    if (path.startsWith('/landing-ntrip-caster')) {
      this.productSubnav = 'ntrip';
    } else if (path.startsWith('/landing-rtk-base-analyzer')) {
      this.productSubnav = 'analyzer';
    } else if (path.startsWith('/landing-ppk-downloader')) {
      this.productSubnav = 'ppk';
    } else if (path.startsWith('/geoastra-gnss-receivers')) {
      this.productSubnav = 'receivers';
    } else if (path.startsWith('/geoastra-software')) {
      this.productSubnav = 'software';
    } else {
      this.productSubnav = null;
    }
  }

  goHome(): void {
    this.router.navigate(['/']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openSoftware(): void {
    this.router.navigate(['/geoastra-software']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openSoftwareProduct(anchor: string): void {
    const url = this.router.url || '';
    if (url.startsWith('/geoastra-software')) {
      const elem = document.getElementById(anchor);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    this.router.navigate(['/geoastra-software'], { fragment: anchor }).then(() => {
      setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    });
  }

  openNtrip(): void {
    this.router.navigate(['/landing-ntrip-caster']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openAnalyzer(): void {
    this.router.navigate(['/landing-rtk-base-analyzer']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openPPK(): void {
    this.router.navigate(['/landing-ppk-downloader']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openReceivers(): void {
    this.router.navigate(['/geoastra-gnss-receivers']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  openReceiver(anchor: string): void {
    const url = this.router.url || '';
    const onReceivers = url.startsWith('/geoastra-gnss-receivers');
    if (onReceivers) {
      const elem = document.getElementById(anchor);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    this.router.navigate(['/geoastra-gnss-receivers'], { fragment: anchor }).then(() => {
      setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    });
  }

  loginNtripCaster(): void {
    this.auth.login();
  }

  loginPpkExpert(): void {
    const base = (this.rtkExpertSiteUrl || 'https://rtk-expert.geoastra.com').replace(/\/+$/, '');
    window.location.href = `${base}/?autoLogin=1`;
  }

  navigateTo(anchorId: string): void {
    const url = this.router.url || '';

    if (this.productSubnav === 'software' || url.startsWith('/geoastra-software')) {
      if (anchorId === 'software-overview') {
        const overview = document.getElementById('software-overview');
        if (overview) {
          overview.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        this.openSoftware();
        return;
      }
      this.openSoftwareProduct(anchorId);
      return;
    }

    if (this.productSubnav === 'receivers' || url.startsWith('/geoastra-gnss-receivers')) {
      if (anchorId === 'receivers-overview') {
        const overview = document.getElementById('receivers-overview');
        if (overview) {
          overview.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        this.openReceivers();
        return;
      }
      this.openReceiver(anchorId);
      return;
    }

    if (this.productSubnav === 'ntrip' || url.startsWith('/landing-ntrip-caster')) {
      const elem = document.getElementById(anchorId);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.router.navigate(['/landing-ntrip-caster'], { fragment: anchorId }).then(() => {
        setTimeout(() => document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      });
      return;
    }
    if (this.productSubnav === 'analyzer' || url.startsWith('/landing-rtk-base-analyzer')) {
      const elem = document.getElementById(anchorId);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.router.navigate(['/landing-rtk-base-analyzer'], { fragment: anchorId }).then(() => {
        setTimeout(() => document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      });
      return;
    }
    if (this.productSubnav === 'ppk' || url.startsWith('/landing-ppk-downloader')) {
      const elem = document.getElementById(anchorId);
      if (elem) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.router.navigate(['/landing-ppk-downloader'], { fragment: anchorId }).then(() => {
        setTimeout(() => document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      });
      return;
    }
    this.router.navigate(['/landing-ntrip-caster'], { fragment: anchorId }).then(() => {
      setTimeout(() => document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    });
  }

  signup(): void {
    this.auth.signup();
  }
}
